import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Check } from 'lucide-react';
import { COUNTRIES } from '../constants/countries';
import { useAuth } from '../App';

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [token, setToken] = useState('');
  const [gUser, setGUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [role, setRole]       = useState('researcher');
  const [org, setOrg]         = useState('');
  const [phone, setPhone]     = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = searchParams.get('token') || sessionStorage.getItem('g_complete_token') || '';
    if (!t) { setInvalid(true); setLoadingUser(false); return; }
    setToken(t);

    axios.get('/api/lab-list/auth/me', { headers: { Authorization: `Bearer ${t}` } })
      .then(res => {
        const u = res.data;
        setGUser(u);
        if (u.user_type) setRole(u.user_type);
        if (!u.needs_profile_completion && u.organization && u.phone) {
          // already completed elsewhere — log in and go home
          sessionStorage.setItem('lab_session_token', t);
          sessionStorage.setItem('biomall_lab_user', JSON.stringify({ ...u, role: u.user_type }));
          login({ ...u, role: u.user_type }, t, 'lab');
          navigate('/', { replace: true });
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoadingUser(false));
  }, [searchParams, navigate, login]);

  const handleSubmit = async () => {
    if (!org.trim())   { toast.error('Organisation name is required'); return; }
    if (!phone.trim()) { toast.error('Phone number is required'); return; }
    if (!country)       { toast.error('Country is required'); return; }
    if (!city.trim())   { toast.error('City is required'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post('/api/lab-list/auth/google/complete-profile', {
        organization: org, user_type: role, phone, city, country,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { user } = res.data;
      sessionStorage.setItem('lab_session_token', token);
      sessionStorage.setItem('biomall_lab_user', JSON.stringify({ ...user, role: user.user_type }));
      login({ ...user, role: user.user_type }, token, 'lab');
      toast.success('Account created! Welcome to BioMall.');
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    }
    setSubmitting(false);
  };

  if (loadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#EEF4FF' }}>
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" data-testid="complete-profile-loading" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#EEF4FF' }}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="complete-profile-invalid">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link expired</h2>
          <p className="text-slate-600 mb-6 text-sm">This profile link is no longer valid. Please sign in again to continue.</p>
          <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/')}>Back to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#EEF4FF' }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="complete-profile-page">
        <div className="flex items-center justify-center gap-2 mb-5">
          <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#DBEAFE', color: '#1D4ED8' }} data-testid="complete-profile-step-badge">
            Step 2 of 2
          </span>
        </div>

        <h1 className="text-xl font-bold text-slate-900 text-center mb-1">Just a few more details</h1>
        <p className="text-sm text-slate-500 text-center mb-6">Finish setting up your BioMall.ai account</p>

        {gUser && (
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl mb-6">
            {gUser.picture && <img src={gUser.picture} alt="" className="w-10 h-10 rounded-full" />}
            <div>
              <p className="text-sm font-semibold text-slate-800">{gUser.name}</p>
              <p className="text-xs text-slate-500">{gUser.email}</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">I am a *</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'researcher',   label: 'Buyer',        sub: 'I source / buy' },
                { value: 'distributor',  label: 'Distributor',  sub: 'I sell & supply' },
                { value: 'manufacturer', label: 'Manufacturer', sub: 'I manufacture' },
              ].map(r => (
                <button key={r.value} type="button" onClick={() => setRole(r.value)}
                  data-testid={`complete-profile-role-${r.value}`}
                  className={`p-2.5 border-2 rounded-xl text-left transition-all ${role === r.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <div className="text-xs font-bold text-gray-900">{r.label}</div>
                  <div className="text-xs text-gray-500">{r.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Organisation / Lab Name *</Label>
            <Input value={org} onChange={e => setOrg(e.target.value)} placeholder="e.g. IIT Bombay, Cipla Ltd, Apollo Hospitals" className="mt-1" data-testid="complete-profile-org" />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" className="mt-1" data-testid="complete-profile-phone" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Country *</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="mt-1" data-testid="complete-profile-country"><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent className="z-[10000]">
                  {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City *</Label>
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Mumbai" className="mt-1" data-testid="complete-profile-city" />
            </div>
          </div>

          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 mt-2"
            onClick={handleSubmit}
            disabled={submitting || !org.trim() || !phone.trim() || !country || !city.trim()}
            data-testid="complete-profile-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Complete Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}
